import { HttpInterceptorFn, HttpErrorResponse, HttpRequest, HttpHandlerFn, HttpEvent, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../auth.service';
import { LoginService } from '../login.service';
import { BehaviorSubject, Observable, throwError, of } from 'rxjs';
import { catchError, switchMap, filter, take } from 'rxjs/operators';

// Armazenamento para token em refresh
let isRefreshing = false;
let refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

export const AuthInterceptor: HttpInterceptorFn = (request: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const loginService = inject(LoginService);
  
  // Recupera token usando o LoginService existente
  const token = loginService.getToken();

  // Clone da request para adicionar headers
  let authRequest = request;

  // ✅ Tratamento especial para endpoint de soft-delete
  if (request.url.includes('/document/soft-delete/')) {
    authRequest = request.clone({
      setHeaders: token ? { Authorization: `Bearer ${token}` } : {},
      responseType: 'text' as 'json'
    });
    
    return next(authRequest).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 200) {
          console.log('Delete bem-sucedido (status 200)');
          return of(new HttpResponse({
            status: 200,
            statusText: 'OK',
            body: { success: true, message: 'Documento excluído com sucesso' },
            url: error.url || ''
          }) as any);
        }
        return throwError(() => error);
      })
    );
  }

  // Adiciona token à requisição
  if (token) {
    authRequest = request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  return next(authRequest).pipe(
    catchError((error: HttpErrorResponse) => {
      // ✅ Ignora erros de parsing em respostas 200
      if (error.status === 200 && error.error instanceof ProgressEvent) {
        console.warn('Erro de parsing na resposta (status 200):', request.url);
        return of(new HttpResponse({
          status: 200,
          statusText: 'OK',
          body: { success: true },
          url: error.url || ''
        }) as any);
      }

      // 🔄 Fluxo de refresh token para erro 401
      if (error.status === 401) {
        console.warn('Token inválido ou expirado. Tentando refresh...');
        
        // Se não tem refresh token, redireciona para login
        if (!authService.hasRefreshToken()) {
          return handleUnauthorized(router, authService);
        }

        // Se já está fazendo refresh, coloca na fila
        if (isRefreshing) {
          return refreshTokenSubject.pipe(
            filter(token => token !== null),
            take(1),
            switchMap(newToken => {
              return next(addTokenToRequest(request, newToken!));
            })
          );
        }

        // Inicia o refresh
        isRefreshing = true;
        refreshTokenSubject.next(null);

        return authService.refreshToken().pipe(
          switchMap((response: any) => {
            isRefreshing = false;
            
            // Salva novos tokens
            if (response.accessToken) {
              authService.setAccessToken(response.accessToken);
            }
            if (response.refreshToken) {
              authService.setRefreshToken(response.refreshToken);
            }
            
            // Notifica observadores
            const newToken = response.accessToken || authService.getAccessToken();
            refreshTokenSubject.next(newToken);
            
            // Reexecuta a requisição original
            return next(addTokenToRequest(request, newToken!));
          }),
          catchError((refreshError: HttpErrorResponse) => {
            isRefreshing = false;
            refreshTokenSubject.next(null);
            
            // Se refresh falhou, limpa tudo e redireciona
            console.error('Refresh token falhou:', refreshError);
            return handleUnauthorized(router, authService);
          })
        );
      }

      // Tratamento de outros erros
      return handleOtherErrors(error, request, router);
    })
  );
};

// Função auxiliar para adicionar token à requisição
function addTokenToRequest(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return request.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`
    }
  });
}

// Função para lidar com erro 401
function handleUnauthorized(router: Router, authService: AuthService): Observable<never> {
  // Faz logout usando o AuthService
  authService.logout();
  
  // Verifica se já está na página de login para evitar loop
  if (!router.url.includes('/login')) {
    // Mostra mensagem amigável
    setTimeout(() => {
      alert('Sessão expirada. Por favor, faça login novamente.');
    }, 100);
    
    // Redireciona para login
    router.navigate(['/login'], {
      queryParams: { 
        returnUrl: router.url || '/',
        reason: 'session_expired'
      },
      replaceUrl: true
    });
  }
  
  return throwError(() => new Error('Sessão expirada'));
}

// Função para tratamento de outros erros
function handleOtherErrors(
  error: HttpErrorResponse, 
  request: HttpRequest<unknown>, 
  router: Router
): Observable<never> {
  console.error('Erro na requisição:', {
    url: request.url,
    status: error.status,
    message: error.message,
    error: error.error
  });

  switch (error.status) {
    case 403:
      console.warn('Acesso negado. Permissões insuficientes.');
      break;
      
    case 404:
      console.error('Recurso não encontrado:', request.url);
      break;
      
    case 500:
      console.error('Erro interno do servidor');
      setTimeout(() => {
        alert('Erro interno no servidor. Tente novamente mais tarde.');
      }, 100);
      break;
      
    case 0:
      console.error('Erro de conexão ou CORS. Verifique o servidor.');
      setTimeout(() => {
        alert('Erro de conexão. Verifique sua internet.');
      }, 100);
      break;
  }

  return throwError(() => error);
}